// May a registrations snapshot be trusted as THE COMPLETE LIST of entries still
// live for this tournament?
//
// It matters because of what that list is used for. pruneCancelledRegistrations
// (js/app.js) removes every imported player whose source registration is absent
// from it — the only way the arbiter's roster ever learns that somebody used
// their cancel link while nobody had the app open. An incomplete list read as a
// complete one therefore does not fail safe: it deletes real players, and the
// leading-edge save pushes the shortened roster to the cloud before anything can
// correct it.
//
// THE INCOMPLETE LIST IS THE ORDINARY ONE. Firestore is configured with
// persistentLocalCache (js/services/firebase-auth.js), so onSnapshot answers a
// query from IndexedDB first and reaches the server afterwards. That first
// answer holds the registrations THIS DEVICE last saw, which for a school
// tournament is a fraction of them: entries arrive on the booking site and are
// seated by the server (importRegistrationNow in functions/index.js) between one
// opening of the app and the next, so the device has never seen them at all.
// Opening the tournament pruned everyone who had signed up since last time.
//
// Nor does the server snapshot that follows undo it: those entries are already
// `imported`, and the import pass only reconsiders `pending` ones.
//
// So: a cache-only snapshot may fill the review panel, and may not authorise the
// prune. Once the server has answered, this subscription stays authoritative —
// the cached snapshots that follow it are that same server state echoed back
// while offline, not a narrower view of it.
export function registrationsAreComplete({ hadServerSnapshot, fromCache }) {
    return hadServerSnapshot === true || fromCache !== true;
}

/**
 * What to do with a roster when some of its players' registrations are gone.
 *
 * A player whose registration was cancelled is removed — UNLESS they have already been
 * paired. A paired player has games against them: opponents' results, scores and
 * tie-breaks all point at the row, and removing it mid-tournament orphaned them. Such a
 * player stays on the list, marked absent (`status: false`) so they are not paired
 * again, and the arbiter is told. The server does the same when it seats the next
 * person off the waiting list (promoteTarget in functions/lib/waiting-list.js).
 *
 * Pure: returns a plan and changes nothing.
 *
 * @param {object[]} players
 * @param {object[]} rounds         that roster's rounds
 * @param {Set<string>} liveRegIds  registrations that still exist
 * @returns {{ keep: object[], removed: object[], withdrawn: object[] }}
 *   keep — the roster to store (withdrawn players included, with status false);
 *   removed — players dropped; withdrawn — players newly marked absent.
 */
export function planCancelledRegistrations(players, rounds, liveRegIds) {
    const paired = new Set();
    (Array.isArray(rounds) ? rounds : []).forEach(r => (r && r.pairings || []).forEach(p => {
        if (p && p.white) paired.add(String(p.white));
        if (p && p.black) paired.add(String(p.black));
    }));
    const keep = [];
    const removed = [];
    const withdrawn = [];
    (Array.isArray(players) ? players : []).forEach(p => {
        const cancelled = p && p.registrationId && !liveRegIds.has(p.registrationId);
        if (!cancelled) { keep.push(p); return; }
        if (paired.has(String(p.id))) {
            if (p.status === false) { keep.push(p); return; }
            const absent = { ...p, status: false };
            keep.push(absent);
            withdrawn.push(absent);
            return;
        }
        removed.push(p);
    });
    return { keep, removed, withdrawn };
}
